<script setup>
import { ref, computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useCompaniesStore } from 'dashboard/stores/companies';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ContactsForm from 'dashboard/components-next/Contacts/ContactsForm/ContactsForm.vue';
import CompanyCreateDialog from 'dashboard/components-next/Companies/CompanyCreateDialog.vue';

const emit = defineEmits(['create']);

const { t } = useI18n();
const companiesStore = useCompaniesStore();

const dialogRef = ref(null);
const contactsFormRef = ref(null);
const createCompanyDialogRef = ref(null);
const contact = ref(null);

const uiFlags = useMapGetter('contacts/getUIFlags');
const isCreatingContact = computed(() => uiFlags.value.isCreating);
const isCreatingCompany = computed(
  () => companiesStore.getUIFlags.creatingItem
);

const createNewContact = contactItem => {
  contact.value = contactItem;
};

const handleDialogConfirm = async () => {
  const isFormValid = await contactsFormRef.value?.validate();
  if (!isFormValid || !contact.value) return;
  emit('create', {
    ...contact.value,
    labels: contactsFormRef.value?.selectedLabels || [],
  });
};

const onSuccess = () => {
  contactsFormRef.value?.resetForm();
  dialogRef.value.close();
};

const closeDialog = () => {
  dialogRef.value.close();
};

const openCreateCompany = () => {
  createCompanyDialogRef.value?.dialogRef.open();
};

const handleCompanyCreated = async company => {
  const { contactId, ...companyParams } = company;
  try {
    const newCompany = await companiesStore.create(companyParams);
    if (contactId) {
      try {
        await companiesStore.attachContactToCompany(newCompany.id, contactId);
      } catch {
        useAlert(t('COMPANIES.CREATE.MESSAGES.CONTACT_LINK_ERROR'));
      }
    }
    contactsFormRef.value?.setCompanyId(newCompany.id);
    createCompanyDialogRef.value?.onSuccess();
    useAlert(t('COMPANIES.CREATE.MESSAGES.SUCCESS'));
  } catch {
    useAlert(t('COMPANIES.CREATE.MESSAGES.ERROR'));
  }
};

defineExpose({ dialogRef, contactsFormRef, onSuccess });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="3xl"
    overflow-y-auto
    @confirm="handleDialogConfirm"
  >
    <ContactsForm
      ref="contactsFormRef"
      is-new-contact
      @update="createNewContact"
    >
      <template #company-link-action>
        <button
          type="button"
          class="self-start text-xs text-n-brand hover:underline"
          @click="openCreateCompany"
        >
          {{
            t(
              'CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.COMPANY_LINK.CREATE_ACTION'
            )
          }}
        </button>
      </template>
    </ContactsForm>
    <CompanyCreateDialog
      ref="createCompanyDialogRef"
      :is-loading="isCreatingCompany"
      @create="handleCompanyCreated"
    />
    <template #footer>
      <div class="flex items-center w-full gap-3">
        <Button
          type="submit"
          :label="
            t('CONTACTS_LAYOUT.HEADER.ACTIONS.CONTACT_CREATION.SAVE_CONTACT')
          "
          color="blue"
          :is-loading="isCreatingContact"
        />
        <Button
          :label="t('DIALOG.BUTTONS.CANCEL')"
          variant="faded"
          color="slate"
          type="reset"
          @click="closeDialog"
        />
      </div>
    </template>
  </Dialog>
</template>
